import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api, { resolveMediaUrl } from '../lib/api';
import { startRecorder, isEmptyRecording, uploadErrorMessage, postAudio, fileNameFor } from '../lib/recording';
import { NoAudioPanel } from '../components/speaking/NoAudioPanel';
import { CheckCircle, XCircle, Lightbulb, Square } from 'lucide-react';
import { toast } from 'sonner';
import { RichText } from '../components/RichTextEditor';
import SuggestedChanges from '../components/SuggestedChanges';

const CELPIP = {
  headerBg: '#e8e8e8', headerBorder: '#ccc', headerText: '#333',
  blue: '#0066cc', timerRed: '#cc0000', infoBg: '#3b82f6',
  instructionBlue: '#0066cc', panelBorder: '#ccc', textColor: '#333',
};

function InfoIcon() {
  return <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '20px', height: '20px', borderRadius: '50%', background: CELPIP.infoBg, color: '#fff', fontSize: '13px', fontWeight: 'bold', marginRight: '8px', flexShrink: 0 }}>i</span>;
}

export default function PracticePart() {
  const { skill, partId } = useParams();
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [selected, setSelected] = useState('');
  const [checked, setChecked] = useState(false);
  const [result, setResult] = useState(null);
  const [writingText, setWritingText] = useState('');
  const [writingResult, setWritingResult] = useState(null);
  const [picks, setPicks] = useState({});
  const [scores, setScores] = useState({ correct: 0, total: 0 });
  const [showInstructions, setShowInstructions] = useState(true);
  const [scoring, setScoring] = useState(false);

  useEffect(() => {
    api.get(`/api/practice/${partId}?limit=20`).then(r => setQuestions(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [partId]);

  const q = questions[currentIdx];
  const partInfo = questions[0]?.part_id || partId;
  const skillLabel = skill?.charAt(0).toUpperCase() + skill?.slice(1) || '';

  const checkAnswer = async () => {
    if (!q) return;
    setScoring(true);
    if (q.question_type === 'mcq') {
      try {
        const { data } = await api.post('/api/practice/check-answer', { question_id: q.id, selected_option: selected });
        setResult(data);
        setChecked(true);
        setScores(prev => ({ correct: prev.correct + (data.is_correct ? 1 : 0), total: prev.total + 1 }));
      } catch {}
    } else if (q.question_type === 'writing_email' || q.question_type === 'writing_survey') {
      try {
        const { data } = await api.post('/api/practice/score-writing', { question_id: q.id, text_response: writingText }, { timeout: 120000 });
        setWritingResult(data);
        setChecked(true);
      } catch (e) {
        // 503 = scorer busy (server already retried once) — the answer stays in the box, student taps Score again.
        toast.error(e.response?.data?.detail || 'Scoring failed — please try again in a moment.');
      }
    } else if (q.question_type === 'dropdown_paragraph') {
      try {
        const { data } = await api.post('/api/practice/check-answer', { question_id: q.id, selected_options: picks });
        setResult(data);
        setChecked(true);
        setScores(prev => ({ correct: prev.correct + (data.correct_count || 0), total: prev.total + (data.total_statements || 0) }));
      } catch {}
    }
    setScoring(false);
  };

  const nextQuestion = () => {
    if (currentIdx < questions.length - 1) {
      setCurrentIdx(currentIdx + 1);
      setSelected('');
      setChecked(false);
      setResult(null);
      setWritingText('');
      setWritingResult(null);
      setPicks({});
    }
  };

  if (loading) return <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Arial, sans-serif' }}><div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" /></div>;

  if (showInstructions) {
    return (
      <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', fontFamily: 'Arial, Helvetica, sans-serif' }} data-testid="practice-instructions">
        {/* Company Header */}
        <div style={{ background: '#1a1a2e', padding: '6px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }} onClick={() => navigate(`/practice/${skill}`)}>
            <img src="/logo-navbar-dark.png" alt="CELPIPMax" style={{ width: '24px', height: '24px' }} />
            <span style={{ color: '#fff', fontWeight: 'bold', fontSize: '13px' }}>CELPIPMax</span>
          </div>
          <span style={{ color: '#94a3b8', fontSize: '12px', cursor: 'pointer' }} onClick={() => navigate(`/practice/${skill}`)}>Back to {skillLabel}</span>
        </div>
        {/* CELPIP Header */}
        <div style={{ background: CELPIP.headerBg, borderBottom: `1px solid ${CELPIP.headerBorder}`, padding: '8px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', minHeight: '44px' }}>
          <span style={{ fontWeight: 'bold', fontSize: '14px', color: CELPIP.headerText }}>Practice - {skillLabel}</span>
          <button onClick={() => setShowInstructions(false)} style={{ background: CELPIP.blue, color: '#fff', border: 'none', padding: '6px 18px', fontWeight: 'bold', fontSize: '13px', cursor: 'pointer' }} data-testid="practice-start-btn">NEXT</button>
        </div>
        {/* Instructions */}
        <div style={{ flex: 1, padding: '24px 40px', background: '#fff', overflowY: 'auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px' }}>
            <InfoIcon />
            <span style={{ fontWeight: 'bold', fontSize: '16px', color: CELPIP.headerText }}>{skillLabel} Practice Instructions</span>
          </div>
          <ul style={{ listStyle: 'none', padding: '0 0 0 28px', margin: 0 }}>
            {[
              `You are practicing ${skillLabel} questions. Take your time to answer each question carefully.`,
              'Click NEXT to move to the next question after answering.',
              'You will see instant feedback and explanations for each question.',
              `This practice session contains ${questions.length} questions from Part ${partId}.`,
            ].map((item, i) => (
              <li key={i} style={{ color: CELPIP.instructionBlue, fontSize: '15px', lineHeight: '1.8', marginBottom: '8px', paddingBottom: '8px', borderBottom: '1px solid #e8e8e8' }}>
                <span style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                  <span style={{ color: CELPIP.instructionBlue, fontWeight: 'bold', fontSize: '18px', lineHeight: '1.2' }}>&bull;</span>
                  <span>{item}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>
        <div style={{ background: CELPIP.headerBg, borderTop: `1px solid ${CELPIP.headerBorder}`, padding: '8px 16px', display: 'flex', justifyContent: 'flex-end', minHeight: '40px' }}>
          <button onClick={() => navigate(`/practice/${skill}`)} style={{ background: CELPIP.blue, color: '#fff', border: 'none', padding: '6px 18px', fontWeight: 'bold', fontSize: '13px', cursor: 'pointer' }}>BACK</button>
        </div>
      </div>
    );
  }

  if (!q) {
    return (
      <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Arial, sans-serif', textAlign: 'center' }}>
        <div>
          <p style={{ color: '#666', marginBottom: '16px' }}>No questions available for this part yet.</p>
          <Link to={`/practice/${skill}`} style={{ color: CELPIP.blue }}>Back to {skillLabel}</Link>
        </div>
      </div>
    );
  }

  const hasPassage = !!q.passage_text;
  const wordCount = writingText.trim() ? writingText.trim().split(/\s+/).length : 0;

  const isMcq = q.question_type === 'mcq';
  const isWriting = q.question_type === 'writing_email' || q.question_type === 'writing_survey';
  const isDropdownPara = q.question_type === 'dropdown_paragraph';
  const isSpeaking = q.question_type === 'speaking_record';
  const statementsCount = (q.statements || []).length;
  const allStatementsPicked = isDropdownPara && statementsCount > 0 && Object.keys(picks).filter(k => picks[k]).length >= statementsCount;
  const canCheck = isMcq ? !!selected : isDropdownPara ? allStatementsPicked : isWriting ? !!writingText.trim() : false;
  const checkLabel = (isMcq || isDropdownPara) ? 'CHECK' : 'GET AI SCORE';

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', fontFamily: 'Arial, Helvetica, sans-serif' }} data-testid="practice-part-page">
      {/* Company Header */}
      <div style={{ background: '#1a1a2e', padding: '6px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }} onClick={() => navigate(`/practice/${skill}`)}>
          <div style={{ width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><img src="/logo-navbar-dark.png" alt="CELPIPMax" style={{ width: '24px', height: '24px' }} /></div>
          <span style={{ color: '#fff', fontWeight: 'bold', fontSize: '13px' }}>CELPIPMax</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ color: '#64748b', fontSize: '12px' }}>{scores.correct}/{scores.total} correct</span>
          <span style={{ color: '#94a3b8', fontSize: '12px', cursor: 'pointer' }} onClick={() => navigate(`/practice/${skill}`)}>Exit</span>
        </div>
      </div>

      {/* CELPIP Header */}
      <div style={{ background: CELPIP.headerBg, borderBottom: `1px solid ${CELPIP.headerBorder}`, padding: '8px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', minHeight: '44px' }}>
        <span style={{ fontWeight: 'bold', fontSize: '14px', color: CELPIP.headerText }}>
          Practice - {skillLabel} Part {partId?.replace(/[A-Z]/g, '')}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ fontSize: '13px', color: '#888' }}>Question {currentIdx + 1} of {questions.length}</span>
          {isSpeaking ? (
            <button onClick={nextQuestion} disabled={currentIdx >= questions.length - 1}
              style={{ background: CELPIP.blue, color: '#fff', border: 'none', padding: '6px 18px', fontWeight: 'bold', fontSize: '13px', cursor: 'pointer', opacity: currentIdx >= questions.length - 1 ? 0.5 : 1 }}
              data-testid="practice-next-btn">NEXT</button>
          ) : checked ? (
            <button onClick={nextQuestion} disabled={currentIdx >= questions.length - 1}
              style={{ background: CELPIP.blue, color: '#fff', border: 'none', padding: '6px 18px', fontWeight: 'bold', fontSize: '13px', cursor: 'pointer', opacity: currentIdx >= questions.length - 1 ? 0.5 : 1 }}
              data-testid="practice-next-btn">NEXT</button>
          ) : (
            <button onClick={checkAnswer} disabled={!canCheck || scoring}
              style={{ background: '#16a34a', color: '#fff', border: 'none', padding: '6px 18px', fontWeight: 'bold', fontSize: '13px', cursor: 'pointer', opacity: (!canCheck || scoring) ? 0.5 : 1, display: 'flex', alignItems: 'center', gap: '6px' }}
              data-testid="check-answer-btn">
              {scoring && <span style={{ display: 'inline-block', width: '14px', height: '14px', border: '2px solid #fff', borderTop: '2px solid transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />}
              {scoring ? 'Scoring...' : checkLabel}
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {/* Left: Passage (if reading) */}
        {hasPassage && (
          <div style={{ width: '50%', borderRight: `1px solid ${CELPIP.panelBorder}`, padding: '20px', overflowY: 'auto', background: '#fff' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '16px' }}>
              <InfoIcon /><span style={{ color: CELPIP.instructionBlue, fontWeight: 'bold', fontSize: '14px' }}>Read the following message.</span>
            </div>
            <div style={{ fontSize: '14px', color: CELPIP.textColor, lineHeight: '1.7', whiteSpace: 'pre-wrap' }}><RichText value={q.passage_text}/></div>
          </div>
        )}

        {/* Right: Question */}
        <div style={{ width: hasPassage ? '50%' : '100%', padding: '20px', overflowY: 'auto', background: '#fff' }}>
          {q.question_type === 'mcq' && (
            <>
              <p style={{ fontSize: '15px', color: CELPIP.textColor, marginBottom: '20px', lineHeight: '1.6' }}><RichText value={q.prompt_text}/></p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {(q.options || []).map(opt => {
                  const isSelected = selected === opt.id;
                  let borderColor = isSelected ? CELPIP.blue : '#ddd';
                  let bgColor = isSelected ? '#e8f0fe' : '#fff';
                  if (checked && result) {
                    if (opt.id === result.correct_answer) { borderColor = '#16a34a'; bgColor = '#dcfce7'; }
                    else if (opt.id === selected && !result.is_correct) { borderColor = '#dc2626'; bgColor = '#fef2f2'; }
                    else { borderColor = '#ddd'; bgColor = '#fff'; }
                  }
                  return (
                    <label key={opt.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px', border: `2px solid ${borderColor}`, borderRadius: '4px', cursor: checked ? 'default' : 'pointer', background: bgColor, transition: 'all 0.15s' }}
                      data-testid={`practice-option-${opt.id}`}>
                      <input type="radio" name={`q-${q.id}`} checked={isSelected} disabled={checked}
                        onChange={() => !checked && setSelected(opt.id)} style={{ width: '18px', height: '18px', accentColor: CELPIP.blue }} />
                      <span style={{ fontSize: '14px', color: CELPIP.textColor }}><strong style={{ marginRight: '6px' }}>{opt.id}.</strong>{opt.text}</span>
                      {checked && opt.id === result?.correct_answer && <CheckCircle size={16} color="#16a34a" style={{ marginLeft: 'auto' }} />}
                      {checked && opt.id === selected && !result?.is_correct && <XCircle size={16} color="#dc2626" style={{ marginLeft: 'auto' }} />}
                    </label>
                  );
                })}
              </div>
              {/* Explanation */}
              {checked && result?.explanation && (
                <div style={{ marginTop: '16px', padding: '14px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '6px' }} data-testid="explanation">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px', color: '#1d4ed8', fontWeight: 'bold', fontSize: '13px' }}>
                    <Lightbulb size={14} />Explanation
                  </div>
                  <p style={{ fontSize: '13px', color: '#1e3a5f', lineHeight: '1.5' }}>{result.explanation}</p>
                </div>
              )}
            </>
          )}

          {(q.question_type === 'writing_email' || q.question_type === 'writing_survey') && (
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '12px' }}>
                <InfoIcon /><span style={{ color: CELPIP.instructionBlue, fontWeight: 'bold', fontSize: '14px' }}>Write your response (150-200 words).</span>
              </div>
              <div style={{ marginBottom: '12px', padding: '12px', background: '#f9f9f9', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px', lineHeight: '1.6', whiteSpace: 'pre-wrap' }}><RichText value={q.prompt_text}/></div>
              <textarea value={writingText} onChange={e => setWritingText(e.target.value)} disabled={checked}
                onPaste={e => e.preventDefault()} spellCheck={false} placeholder="Type your response here..."
                style={{ flex: 1, width: '100%', padding: '14px', border: '1px solid #aaa', resize: 'none', fontSize: '14px', lineHeight: '1.6', outline: 'none', minHeight: '200px' }}
                data-testid="practice-writing-textarea" />
              <div style={{ marginTop: '6px', fontSize: '13px' }}>
                <span style={{ color: wordCount < 150 ? '#cc6600' : wordCount > 200 ? '#cc0000' : '#006600' }}>Word Count: {wordCount}</span>
              </div>
              {writingResult && (
                <div style={{ marginTop: '12px', padding: '14px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '6px' }} data-testid="writing-ai-feedback">
                  <div style={{ fontWeight: 'bold', color: '#1d4ed8', marginBottom: '8px' }}>AI Score: CLB {writingResult.overall_clb}</div>
                  {Object.entries(writingResult.criteria || {}).map(([k, v]) => (
                    <div key={k} style={{ display: 'flex', gap: '8px', fontSize: '13px', marginBottom: '4px' }}>
                      <span style={{ fontWeight: '600', textTransform: 'capitalize', width: '140px' }}>{k.replace(/_/g, ' ')}:</span>
                      <span style={{ color: '#1d4ed8', fontWeight: 'bold' }}>CLB {v.score}</span>
                      <span style={{ color: '#666' }}>— {v.feedback}</span>
                    </div>
                  ))}
                  {Array.isArray(writingResult.corrections) && writingResult.corrections.length > 0 && (
                    <SuggestedChanges text={writingText} corrections={writingResult.corrections} />
                  )}
                </div>
              )}
            </div>
          )}

          {isDropdownPara && (
            <DropdownParaPractice
              q={q}
              picks={picks}
              setPick={(sid, v) => !checked && setPicks(p => ({ ...p, [sid]: v }))}
              checked={checked}
              result={result}
            />
          )}

          {isSpeaking && <SpeakingPractice q={q} />}
        </div>
      </div>

      {/* Footer */}
      <div style={{ background: CELPIP.headerBg, borderTop: `1px solid ${CELPIP.headerBorder}`, padding: '8px 16px', display: 'flex', justifyContent: 'space-between', minHeight: '40px' }}>
        <div>
          {/* Progress */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            {questions.map((_, i) => (
              <div key={i} style={{ width: '24px', height: '6px', borderRadius: '3px', background: i < currentIdx ? '#16a34a' : i === currentIdx ? CELPIP.blue : '#ddd', transition: 'background 0.2s' }} />
            ))}
          </div>
        </div>
        <button onClick={() => navigate(`/practice/${skill}`)} style={{ background: CELPIP.blue, color: '#fff', border: 'none', padding: '6px 18px', fontWeight: 'bold', fontSize: '13px', cursor: 'pointer' }}>
          BACK
        </button>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}


/* ─── Dropdown Paragraph (Reading/Listening cloze) practice renderer ─── */
function DropdownParaPractice({ q, picks, setPick, checked, result }) {
  const statements = q.statements || [];
  const options = q.options || [];
  const resultMap = {};
  (result?.statement_results || []).forEach(r => { resultMap[r.statement_id] = r; });
  const optText = (id) => (options.find(o => o.id === id) || {}).text || id;
  const ddStyle = { padding: '4px 22px 4px 8px', border: '1px solid #b8bec6', borderRadius: '2px', fontSize: '13px', minWidth: '120px', background: '#dde6ee', color: '#1f2937', cursor: checked ? 'default' : 'pointer', appearance: 'auto' };

  return (
    <div data-testid="practice-dropdown-paragraph">
      <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '14px' }}>
        <InfoIcon />
        <span style={{ color: CELPIP.instructionBlue, fontWeight: 'bold', fontSize: '14px' }}>
          {q.prompt_text && q.prompt_text.trim()
            ? <RichText value={q.prompt_text} />
            : 'Choose the best way to complete each statement from the drop-down menu.'}
        </span>
      </div>
      <div style={{ maxWidth: '760px' }}>
        {statements.map((s, i) => {
          const stmtOpts = (Array.isArray(s.option_ids) && s.option_ids.length > 0)
            ? options.filter(o => s.option_ids.includes(o.id))
            : options;
          const sel = picks[s.id] || '';
          const r = resultMap[s.id];
          const parts = (s.text || '').split(/___|\{blank\}/);
          const isLast = i === statements.length - 1;
          const dd = (
            <select value={sel} disabled={checked} onChange={e => setPick(s.id, e.target.value)} style={ddStyle} data-testid={`practice-dd-select-${i}`}>
              <option value=""></option>
              {stmtOpts.map(o => <option key={o.id} value={o.id}>{o.text}</option>)}
            </select>
          );
          return (
            <div key={s.id || i} style={{ fontSize: '14px', color: CELPIP.textColor, lineHeight: '2.2', padding: '10px 0', borderBottom: isLast ? 'none' : '1px solid #e8eaed' }} data-testid={`practice-dd-statement-${i}`}>
              <span style={{ marginRight: '6px' }}>{i + 1}.</span>
              {parts.length > 1
                ? parts.map((p, pi) => (
                    <React.Fragment key={pi}>
                      <span style={{ whiteSpace: 'pre-wrap' }}>{p}</span>
                      {pi < parts.length - 1 && dd}
                    </React.Fragment>
                  ))
                : <><span style={{ whiteSpace: 'pre-wrap' }}>{s.text} </span>{dd}</>
              }
              {checked && r && (
                r.is_correct
                  ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', marginLeft: '8px', color: '#16a34a', fontSize: '13px', fontWeight: 600 }}><CheckCircle size={15} /> Correct</span>
                  : <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', marginLeft: '8px', color: '#dc2626', fontSize: '13px', fontWeight: 600 }}><XCircle size={15} /> Correct answer: {optText(r.correct_option_id)}</span>
              )}
            </div>
          );
        })}
      </div>
      {checked && result && (
        <div style={{ marginTop: '14px', padding: '12px 14px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '6px' }} data-testid="practice-dd-result">
          <div style={{ fontWeight: 'bold', color: '#1d4ed8', fontSize: '13px', marginBottom: result.explanation ? '6px' : 0 }}>
            Score: {result.correct_count}/{result.total_statements} correct
          </div>
          {result.explanation && <p style={{ fontSize: '13px', color: '#1e3a5f', lineHeight: '1.5' }}>{result.explanation}</p>}
        </div>
      )}
    </div>
  );
}

/* ─── Speaking practice — record → upload → synchronous AI score ─── */
function SpeakingPractice({ q }) {
  const [phase, setPhase] = useState('idle'); // idle | prep | recording | scoring | done | failed | mic_denied
  const [pt, setPt] = useState(q?.prep_time_seconds || 30);
  const [rt, setRt] = useState(q?.response_time_seconds || 60);
  const [localURL, setLocalURL] = useState(null);
  const [serverURL, setServerURL] = useState(null);
  const [transcript, setTranscript] = useState('');
  const [aiScore, setAiScore] = useState(null);
  const [error, setError] = useState('');
  const mr = useRef(null);
  const lastBlob = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => () => {
    try {
      if (mr.current && mr.current.state === 'recording') mr.current.stop();
      mr.current?.stream?.getTracks()?.forEach(t => t.stop());
      streamRef.current?.getTracks()?.forEach(t => t.stop());
    } catch {}
    try { if (localURL) URL.revokeObjectURL(localURL); } catch {}
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (phase === 'prep' && pt > 0) { const t = setTimeout(() => setPt(p => p - 1), 1000); return () => clearTimeout(t); }
    if (phase === 'prep' && pt <= 0) startRec();
    // eslint-disable-next-line
  }, [phase, pt]);
  useEffect(() => {
    if (phase === 'recording' && rt > 0) { const t = setTimeout(() => setRt(r => r - 1), 1000); return () => clearTimeout(t); }
    if (phase === 'recording' && rt <= 0) stopRec();
    // eslint-disable-next-line
  }, [phase, rt]);

  const beginPrep = async () => {
    try {
      const s = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = s;
      setPt(q?.prep_time_seconds || 30);
      setPhase('prep');
    } catch (e) {
      setPhase('mic_denied');
    }
  };

  const startRec = async () => {
    try {
      let s = streamRef.current;
      if (!s || s.getTracks().every(t => t.readyState !== 'live')) {
        s = await navigator.mediaDevices.getUserMedia({ audio: true });
        streamRef.current = s;
      }
      mr.current = startRecorder(s, async (blob) => {
        s.getTracks().forEach(t => t.stop());
        streamRef.current = null;
        if (isEmptyRecording(blob)) { setPhase('no_audio'); return; }
        lastBlob.current = blob;
        setLocalURL(URL.createObjectURL(blob));
        await uploadAndScore(blob);
      });
      setRt(q?.response_time_seconds || 60);
      setPhase('recording');
    } catch (e) {
      setPhase('mic_denied');
    }
  };

  const stopRec = () => { if (mr.current?.state === 'recording') mr.current.stop(); };

  const uploadAndScore = async (blob) => {
    setPhase('scoring');
    setError('');
    try {
      const fd = new FormData();
      fd.append('file', blob, fileNameFor(blob.type || ''));
      fd.append('question_id', q?.id || '');
      // The server already retries a busy speech-to-text once; a 503 here means "tap Retry in a moment".
      const res = await postAudio('/api/practice/transcribe-and-score', fd, { attempts: 1, timeout: 180000 });
      const { transcript: tx = '', ai_score = null, audio_url = '' } = res.data || {};
      setTranscript(tx);
      setAiScore(ai_score);
      setServerURL(audio_url);
      setPhase('done');
    } catch (err) {
      setError(uploadErrorMessage(err).replace('Upload failed', 'Upload or scoring failed'));
      setPhase('failed');
    }
  };

  const retry = () => { if (lastBlob.current) uploadAndScore(lastBlob.current); };
  const reset = () => {
    setLocalURL(null); setServerURL(null); setTranscript(''); setAiScore(null); setError('');
    setPt(q?.prep_time_seconds || 30); setRt(q?.response_time_seconds || 60); setPhase('idle');
  };

  const prepSec = q?.prep_time_seconds || 30;
  const recSec = q?.response_time_seconds || 60;
  const playbackURL = serverURL ? resolveMediaUrl(serverURL) : localURL;
  const optionImages = (q?.options || []).filter(o => o.image_url);

  return (
    <div style={{ display: 'flex', gap: '20px', height: '100%' }} data-testid="practice-speaking">
      {/* LEFT: prompt + image(s) */}
      <div style={{ flex: 1, minWidth: 0, overflowY: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '12px' }}>
          <InfoIcon /><span style={{ color: CELPIP.instructionBlue, fontWeight: 'bold', fontSize: '14px', lineHeight: '1.6' }}><RichText value={q?.prompt_text || ''} /></span>
        </div>
        {q?.image_url && <div style={{ textAlign: 'center', marginBottom: '12px' }}><img src={resolveMediaUrl(q.image_url)} alt="Prompt" style={{ maxWidth: '100%', maxHeight: '320px', border: '1px solid #ddd', borderRadius: '4px' }} /></div>}
        {optionImages.length > 0 && (
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '12px' }}>
            {optionImages.map((o, i) => (
              <div key={o.id || i} style={{ flex: '1 1 45%', minWidth: '160px', textAlign: 'center' }}>
                <img src={resolveMediaUrl(o.image_url)} alt={o.label || `Option ${i + 1}`} style={{ maxWidth: '100%', maxHeight: '220px', border: '1px solid #ddd', borderRadius: '4px' }} />
                {o.label && <div style={{ fontSize: '13px', color: CELPIP.textColor, marginTop: '4px', fontWeight: 600 }}>{o.label}</div>}
              </div>
            ))}
          </div>
        )}
        <div style={{ fontSize: '12px', color: '#c0392b', fontStyle: 'italic', fontWeight: 'bold' }}>*This practice records your response for AI scoring.</div>
      </div>

      {/* RIGHT: recording interface */}
      <div style={{ flex: 1, minWidth: 0, background: '#f8fafc', borderRadius: '8px', border: '1px solid #e5e7eb', padding: '24px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', overflowY: 'auto' }}>
        {phase === 'idle' && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '14px', color: '#475569', marginBottom: '8px' }}>Preparation: <strong>{prepSec}s</strong> &nbsp; Recording: <strong>{recSec}s</strong></div>
            <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '18px', maxWidth: '320px' }}>When you click Start, you'll get {prepSec}s to prepare, then recording begins automatically.</p>
            <button onClick={beginPrep} style={{ background: CELPIP.blue, color: '#fff', border: 'none', padding: '12px 28px', fontWeight: 'bold', fontSize: '14px', borderRadius: '6px', cursor: 'pointer' }} data-testid="speaking-begin-btn">
              Start Preparation
            </button>
          </div>
        )}
        {phase === 'prep' && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ background: '#fff', borderRadius: '12px', padding: '24px 40px', border: '1px solid #ddd', marginBottom: '16px' }}>
              <div style={{ fontSize: '15px', fontWeight: 'bold', color: '#1e293b' }}>Preparation Time</div>
              <div style={{ fontSize: '40px', fontWeight: 'bold', color: CELPIP.blue }}>{pt}</div>
            </div>
            <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '14px' }}>Recording will begin automatically</div>
            <button onClick={startRec} style={{ background: '#3b82f6', color: '#fff', border: 'none', padding: '10px 22px', fontWeight: 'bold', fontSize: '14px', borderRadius: '4px', cursor: 'pointer' }} data-testid="speaking-start-now-btn">
              Start Recording Now
            </button>
          </div>
        )}
        {phase === 'recording' && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ width: '120px', height: '120px', borderRadius: '50%', background: '#fde8e8', border: '5px solid #e74c3c', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 18px', boxShadow: '0 0 0 8px rgba(231,76,60,0.15)' }}>
              <span style={{ fontSize: '40px', fontWeight: 'bold', color: '#c0392b' }}>{rt}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '12px' }}>
              <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#e74c3c', animation: 'pulse 1s infinite' }} />
              <span style={{ fontSize: '15px', fontWeight: 'bold', color: '#e74c3c' }}>Recording...</span>
            </div>
            <button onClick={stopRec} style={{ background: '#e74c3c', color: '#fff', border: 'none', padding: '9px 24px', fontWeight: 'bold', fontSize: '14px', borderRadius: '4px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '6px' }} data-testid="speaking-stop-btn">
              <Square size={14} /> Stop Recording
            </button>
          </div>
        )}
        {phase === 'scoring' && (
          <div style={{ textAlign: 'center', width: '100%', maxWidth: '360px' }} data-testid="speaking-scoring">
            <div style={{ width: '64px', height: '64px', borderRadius: '50%', border: '5px solid #dbeafe', borderTopColor: CELPIP.blue, margin: '0 auto 14px', animation: 'spin 1s linear infinite' }} />
            <div style={{ fontSize: '15px', fontWeight: 'bold', color: '#1e293b', marginBottom: '6px' }}>Transcribing &amp; scoring…</div>
            <div style={{ fontSize: '12px', color: '#64748b' }}>This can take 10-40 seconds. Please don't close this tab.</div>
            {localURL && <audio controls src={localURL} style={{ width: '100%', marginTop: '14px' }} />}
          </div>
        )}
        {phase === 'failed' && (
          <div style={{ textAlign: 'center', width: '100%', maxWidth: '360px' }} data-testid="speaking-failed">
            <XCircle size={48} color="#dc2626" style={{ margin: '0 auto 12px' }} />
            <div style={{ fontSize: '15px', fontWeight: 'bold', color: '#dc2626', marginBottom: '8px' }}>Scoring failed</div>
            <div style={{ fontSize: '13px', color: '#475569', marginBottom: '14px' }}>{error}</div>
            {localURL && <audio controls src={localURL} style={{ width: '100%', marginBottom: '12px' }} />}
            <button onClick={retry} style={{ background: CELPIP.blue, color: '#fff', border: 'none', padding: '10px 26px', fontWeight: 'bold', fontSize: '14px', borderRadius: '4px', cursor: 'pointer' }} data-testid="speaking-retry-btn">Retry</button>
          </div>
        )}
        {phase === 'no_audio' && (
          <NoAudioPanel onRecordAgain={beginPrep} testIdPrefix="practice-no-audio" />
        )}
        {phase === 'mic_denied' && (
          <div style={{ textAlign: 'center', width: '100%', maxWidth: '360px' }} data-testid="speaking-mic-denied">
            <div style={{ fontSize: '34px', marginBottom: '10px' }}>&#128265;</div>
            <div style={{ fontSize: '15px', fontWeight: 'bold', color: '#b45309', marginBottom: '8px' }}>Microphone not available</div>
            <div style={{ fontSize: '13px', color: '#475569', marginBottom: '14px', lineHeight: '1.55' }}>Your browser blocked microphone access. Allow it from the address bar, then tap below.</div>
            <button onClick={beginPrep} style={{ background: '#2563eb', color: '#fff', border: 'none', padding: '10px 24px', fontWeight: 'bold', fontSize: '13px', borderRadius: '4px', cursor: 'pointer' }} data-testid="speaking-mic-retry-btn">Enable Microphone</button>
          </div>
        )}
        {phase === 'done' && (
          <div style={{ width: '100%' }} data-testid="speaking-done">
            <div style={{ textAlign: 'center', marginBottom: '14px' }}>
              <CheckCircle size={40} color="#16a34a" style={{ margin: '0 auto 8px' }} />
              <div style={{ fontSize: '15px', fontWeight: 'bold', color: '#16a34a' }}>Response Recorded</div>
            </div>
            {playbackURL && <audio controls src={playbackURL} style={{ width: '100%', marginBottom: '12px' }} data-testid="speaking-playback" />}
            {aiScore && (
              <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '6px', padding: '14px' }} data-testid="speaking-ai-feedback">
                <div style={{ fontWeight: 'bold', color: '#1d4ed8', marginBottom: '8px' }}>
                  AI Score: CLB {aiScore.overall_clb ?? '—'}{aiScore.estimated_level ? ` (${aiScore.estimated_level})` : ''}
                </div>
                {Object.entries(aiScore.criteria || {}).map(([k, v]) => (
                  <div key={k} style={{ display: 'flex', gap: '8px', fontSize: '13px', marginBottom: '4px', flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 600, textTransform: 'capitalize', minWidth: '130px' }}>{k.replace(/_/g, ' ')}:</span>
                    <span style={{ color: '#1d4ed8', fontWeight: 'bold' }}>CLB {v.score}</span>
                    <span style={{ color: '#475569' }}>— {v.feedback}</span>
                  </div>
                ))}
                {Array.isArray(aiScore.key_suggestions) && aiScore.key_suggestions.length > 0 && (
                  <div style={{ marginTop: '10px' }}>
                    <div style={{ fontWeight: 600, fontSize: '13px', color: '#1e293b', marginBottom: '4px' }}>Suggestions:</div>
                    <ul style={{ margin: 0, paddingLeft: '18px' }}>
                      {aiScore.key_suggestions.map((s, i) => <li key={i} style={{ fontSize: '13px', color: '#475569', marginBottom: '2px' }}>{s}</li>)}
                    </ul>
                  </div>
                )}
              </div>
            )}
            {transcript && (
              <details style={{ marginTop: '12px' }} data-testid="speaking-transcript">
                <summary style={{ cursor: 'pointer', fontSize: '13px', fontWeight: 600, color: '#1d4ed8' }}>View transcript</summary>
                <p style={{ fontSize: '13px', color: '#475569', lineHeight: '1.6', marginTop: '6px', whiteSpace: 'pre-wrap' }}>{transcript}</p>
              </details>
            )}
            <div style={{ textAlign: 'center', marginTop: '14px' }}>
              <button onClick={reset} style={{ background: '#fff', color: CELPIP.blue, border: `1px solid ${CELPIP.blue}`, padding: '8px 20px', fontWeight: 'bold', fontSize: '13px', borderRadius: '4px', cursor: 'pointer' }} data-testid="speaking-redo-btn">Record Again</button>
            </div>
          </div>
        )}
      </div>
      <style>{`@keyframes spin{to{transform:rotate(360deg);}}@keyframes pulse{0%,100%{opacity:1;}50%{opacity:0.3;}}`}</style>
    </div>
  );
}
